import React from 'react';
import { useAuditLog } from '../lib/ledger';

// Hidden compliance panel — renders only when ?audit=1 is present in the
// URL or the 'agoracle_audit_unlock' flag has been dropped into
// localStorage. Shows the last five ledger entries for the active user.

function isAuditUnlocked(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('audit') === '1') return true;
    return window.localStorage.getItem('agoracle_audit_unlock') === '1';
  } catch {
    return false;
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month:  'short',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

function fmtAmount(oc: number): string {
  const sign = oc > 0 ? '+' : oc < 0 ? '−' : '';
  return `${sign}${Math.abs(oc).toFixed(2)} OC`;
}

interface Props { userId: string; }

export default function AuditLog({ userId }: Props): React.JSX.Element | null {
  const entries = useAuditLog(userId, 5);
  if (!isAuditUnlocked()) return null;

  return (
    <div style={{
      backgroundColor: '#0f172a',
      border:          '1px solid #1e293b',
      borderTop:       '2px solid #a78bfa',
      borderRadius:    8,
      padding:         '14px 16px',
      margin:          '16px 0',
      fontFamily:      'monospace',
      maxWidth:        520,
      // Reserve five-row height so the panel doesn't jerk as realtime
      // pledges and stipends stream in from the ledger.
      minHeight:       200,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <p style={{ color: '#c084fc', fontSize: 10, letterSpacing: '0.22em', margin: 0, fontWeight: 700 }}>
          AUDIT LOG
        </p>
        <p style={{ color: '#475569', fontSize: 9, letterSpacing: '0.14em', margin: 0 }}>
          {userId.toUpperCase()} · LAST {entries.length || 0}
        </p>
      </div>

      {entries.length === 0 ? (
        <p style={{ color: '#475569', fontSize: 11, margin: 0, textAlign: 'center', padding: '14px 0' }}>
          No ledger entries yet.
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
          <thead>
            <tr style={{ color: '#475569', letterSpacing: '0.14em' }}>
              <th style={{ textAlign: 'left',  padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>DATE</th>
              <th style={{ textAlign: 'left',  padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>ACTION</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: '1px solid #1e293b' }}>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => {
              const tone = e.amount > 0 ? '#86efac' : e.amount < 0 ? '#fca5a5' : '#94a3b8';
              return (
                <tr key={i}>
                  <td style={{ color: '#94a3b8', padding: '6px 8px', borderBottom: '1px solid #111a2e', whiteSpace: 'nowrap' }}>
                    {fmtDate(e.date)}
                  </td>
                  <td style={{ color: '#cbd5e1', padding: '6px 8px', borderBottom: '1px solid #111a2e' }}>
                    {e.action}
                  </td>
                  <td style={{ color: tone, padding: '6px 8px', borderBottom: '1px solid #111a2e', textAlign: 'right', fontWeight: 700 }}>
                    {fmtAmount(e.amount)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <p style={{ color: '#334155', fontSize: 9, letterSpacing: '0.18em', margin: '10px 0 0', textAlign: 'right' }}>
        DUMMY POT · LOCAL LEDGER
      </p>
    </div>
  );
}
